import { useRef } from "react";
import { useLang } from "@/contexts/LanguageContext";
import { IMAGES, SITE } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";

function ClientCard({ name, idx }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(700px) rotateX(${(-y * 8).toFixed(2)}deg) rotateY(${(x * 10).toFixed(2)}deg) translateZ(0)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(700px) rotateX(0) rotateY(0)";
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`client-card-${idx}`}
      className="relative h-32 md:h-36 glass hover:border-gold-500/40 flex items-center justify-center transition-transform duration-200 will-change-transform group"
      style={{ transformStyle: "preserve-3d" }}
    >
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500/70 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
      <span className="font-heading font-extrabold text-metallic group-hover:text-gold-shine text-lg md:text-xl tracking-wider px-3 text-center">
        {name}
      </span>
      <span className="absolute bottom-2 right-3 font-mono text-[10px] text-white/35 tracking-widest">
        EAH · {String(idx + 1).padStart(2, "0")}
      </span>
    </div>
  );
}

export default function Clients() {
  const { t, isRTL } = useLang();
  const list = t.clients.list;

  return (
    <>
      <PageHead customTitle={`${t.clients.title} · ${SITE.brand.en}`} customDesc={t.clients.subtitle} />

      <section className="relative pt-40 pb-12 overflow-hidden" data-testid="clients-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.contact} alt="" className="w-full h-full object-cover opacity-20" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <div className="absolute inset-0 grid-blueprint opacity-30" />
          <FloatingObjects density="low" />
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4">{t.clients.overline}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.clients.title}
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.clients.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <Section testId="clients-marquee" className="!py-12">
        <div className="marquee-mask overflow-hidden border-y border-white/5 py-6">
          <div
            className={`flex gap-12 whitespace-nowrap ${isRTL ? "animate-marquee-rtl" : "animate-marquee"}`}
            style={{ width: "max-content" }}
          >
            {[...list, ...list].map((name, idx) => (
              <span
                key={idx}
                className="text-metallic font-heading font-extrabold text-2xl md:text-3xl tracking-wider px-6"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* Grid */}
      <Section testId="clients-grid" className="!pt-4">
        <div className="container-pad">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
            {list.map((name, i) => (
              <RevealItem key={name + i}>
                <ClientCard name={name} idx={i} />
              </RevealItem>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}
